import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import type { DeleteSearchItem } from '../../api/deletes'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useDeleteWizard } from '../../delete/DeleteWizardContext'
import { formatInt } from '../../lib/format'
import { DELETE_STEPS } from './steps'

/* Step 3, ported from "batchbooks_delete_review_modernized".

   The rows are the records POST /api/delete/search resolved from the user's
   filters or reference numbers — real QuickBooks data, not placeholders. The
   checkboxes let the user drop individual records from the deletion set; only
   the kept ones are passed to the confirm step.

   The columns and the cells both come from the server now, resolved against
   the same export template the Export job writes. This page used to translate
   template labels into entity field names and look those up in the raw
   QuickBooks field bag, which never matched: the bag is keyed by QBO's own
   paths ("VendorRef.name", "Line[0].Amount") while the registry names them
   ("VendorRef", "Line.AccountRef"). Every cell fell through to a dash. Letting
   the server expand the record — it already does this for Export — also gets
   the line-per-row shape right, so a journal entry shows one row per posting
   instead of one blank row per entry.

   Selection stays at *record* level even though a record may span several
   rows: the checkbox and status cells use rowSpan and are drawn once per
   record, matching what /api/delete/submit receives. */

/** One table row, plus the record it belongs to and its position within it. */
interface Row {
  cells: string[]
}

/** The cells for one record, falling back to the summary fields when the
 * server sent no expansion (an entity with no export template). */
function cellsFor(row: DeleteSearchItem, columns: string[] | null): Row[] {
  if (row.rows && row.rows.length > 0) {
    return row.rows.map((cells) => ({ cells }))
  }

  // No expansion: derive the single summary row we can still fill. The columns
  // are the template's, so only the handful we can map by hand get a value.
  const byLabel: Record<string, string> = {
    'TRANSACTION DATE': row.txnDate ?? '',
    REFNUMBER: row.docNumber ?? '',
    VENDOR: row.name ?? '',
    CUSTOMER: row.name ?? '',
    'VENDOR NAME': row.name ?? '',
    'CUSTOMER NAME': row.name ?? '',
    AMOUNT: row.amount ?? '',
  }

  const cells = (columns ?? []).map(
    (label) => byLabel[label.toUpperCase()] ?? '',
  )

  return [{ cells: cells.length > 0 ? cells : [row.txnDate ?? '', row.docNumber ?? ''] }]
}

export function DeleteReviewPage() {
  const wizard = useDeleteWizard()
  const navigate = useNavigate()
  const { current } = useCompanies()
  const [search, setSearch] = useState('')
  const [dropped, setDropped] = useState<Set<string>>(() => new Set())

  const subject = wizard.entity?.label ?? wizard.entityType
  const fromRefs = wizard.source === 'refs'
  const rows = (wizard.review ?? []) as DeleteSearchItem[]

  const columns = wizard.exportColumns

  // Searches every visible cell, not just the two summary fields: once a
  // record renders as several lines, the thing the user is hunting for is as
  // likely to be an account or a memo as a document number.
  const visible = rows.filter((row) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    const hay = [
      row.docNumber ?? '',
      row.name ?? '',
      row.txnDate ?? '',
      ...(row.rows ?? []).flat(),
    ]
      .join(' ')
      .toLowerCase()
    return hay.includes(q)
  })

  if (!wizard.entityType) return <Navigate to="/app/delete" replace />

  const idOf = (row: DeleteSearchItem, i: number) => `${row.qboId}-${i}`
  const selected = rows.filter((row, i) => !dropped.has(idOf(row, i)))
  const allShown = visible.length > 0 && visible.every((row) => !dropped.has(idOf(row, rows.indexOf(row))))

  const toggle = (id: string) =>
    setDropped((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setDropped((prev) => {
      const next = new Set(prev)
      for (const row of visible) {
        const id = idOf(row, rows.indexOf(row))
        if (allShown) next.add(id)
        else next.delete(id)
      }
      return next
    })

  const proceed = () => {
    // Carry the kept records to the confirm step.
    wizard.setReview(selected)
    navigate('/app/delete/confirm')
  }

  const headers = columns ?? ['Transaction Date', 'RefNumber', 'Name', 'Amount']

  return (
    <div className="bb bb-wizard bb-wizard-wide bb-delete-review">
      <style>{`
        .bb-delete-review { padding-top: 0; padding-bottom: 8px }
        .bb-delete-review .bb-steps { margin-bottom: 16px }
        .bb-delete-review .bb-activity-head { margin-bottom: 8px }
        .bb-delete-review .bb-rtable td.bb-cell-blank { color: var(--bb-muted) }
        .bb-delete-review .bb-rtable tr.bb-rec-start td { border-top: 1px solid var(--bb-line) }
      `}</style>

      <BbStepper steps={DELETE_STEPS} current={2} />

      <div className="bb-activity-head">
        <div>
          <h2>Review Records for Deletion</h2>
          <p className="bb-map-help">
            These <b>{formatInt(selected.length)}</b>{' '}
            {subject.toLowerCase()} records would be permanently removed from{' '}
            {current?.companyName ?? 'your QuickBooks company'}.
          </p>
        </div>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-quiet" to="/app/delete/filter">
            Previous
          </Link>
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="bb-empty-ref">
          <BbIcon name="search" />
          <h3>Nothing to review</h3>
          <p>Go back and {fromRefs ? 'paste some reference numbers' : 'apply a filter'} first.</p>
        </div>
      ) : (
        <div className="bb-activity" style={{ marginTop: 12 }}>
          <div className="bb-toolbar">
            <input
              className="bb-input"
              type="search"
              placeholder="Search records…"
              aria-label="Search records"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className="bb-toolbar-r">
              Showing {formatInt(visible.length)} of {formatInt(rows.length)} records
            </span>
          </div>

          <div
            style={{
              overflow: 'auto',
              maxHeight: 'max(240px, calc(100dvh - 550px))',
            }}
          >
          <table className="bb-rtable" style={{ minWidth: 'max-content' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th className="bb-check-cell" style={{ position: 'sticky', top: 0 }}>
                  <input
                    className="bb-check"
                    type="checkbox"
                    aria-label="Select all shown records"
                    checked={allShown}
                    onChange={toggleAll}
                  />
                </th>
                {headers.map((label, i) => (
                  <th key={i}>{label}</th>
                ))}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const id = idOf(row, rows.indexOf(row))
                const kept = !dropped.has(id)
                const expanded = cellsFor(row, columns)

                return expanded.map((line, li) => (
                  <tr
                    key={`${id}-${li}`}
                    className={li === 0 ? 'bb-rec-start' : undefined}
                  >
                    {/* Checkbox and status span the record's lines: one record,
                        one decision, however many rows it renders as. */}
                    {li === 0 && (
                      <td className="bb-check-cell" rowSpan={expanded.length}>
                        <input
                          className="bb-check"
                          type="checkbox"
                          aria-label={`Include ${row.docNumber ?? row.qboId}`}
                          checked={kept}
                          onChange={() => toggle(id)}
                        />
                      </td>
                    )}
                    {line.cells.map((value, ci) => (
                      <td
                        key={ci}
                        className={value === '' ? 'bb-cell-blank' : undefined}
                      >
                        {value === '' ? '—' : value}
                      </td>
                    ))}
                    {li === 0 && (
                      <td rowSpan={expanded.length}>
                        {kept ? (
                          <span className="bb-apill bb-apill-ok">
                            <i />
                            Ready to Delete
                          </span>
                        ) : (
                          <span className="bb-apill bb-apill-idle">
                            <i />
                            Skipped
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              })}
            </tbody>
          </table>
          </div>

          <div className="bb-pager">
            <span className="bb-pager-n">
              {formatInt(selected.length)} record{selected.length === 1 ? '' : 's'} selected for deletion
            </span>
          </div>
        </div>
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/delete/filter">
          <BbIcon name="arrow-left" />
          Back
        </Link>
        <span className="bb-wizard-foot-r">
          <button
            className="bb-btn bb-btn-danger"
            type="button"
            disabled={selected.length === 0}
            onClick={proceed}
          >
            Next: Confirm Deletion ({formatInt(selected.length)})
            <BbIcon name="arrow-right" />
          </button>
        </span>
      </div>
    </div>
  )
}
